import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Stack, Chip, Button, Divider
} from '@mui/material';
import { blueGrey, lightBlue, blue, teal, grey } from '@mui/material/colors';
import { motion, useScroll, useTransform } from 'framer-motion';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function ScrollReveal({ children, delay = 0, direction = 'up' }) {
  const variants = {
    up: { hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, x: 0 } },
    left: { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, y: 0, x: 0 } },
    right: { hidden: { opacity: 0, x: 50 }, visible: { opacity: 1, y: 0, x: 0 } },
  };
  return (
    <motion.div
      variants={variants[direction]}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}

const MISSION_VALUES = [
  {
    icon: <EmojiNatureIcon sx={{ fontSize: 40 }} />,
    title: '환경 보호',
    description: '우리는 자원 절약과 재활용을 최우선으로 하고, 지속 가능한 원료를 사용하여 환경에 미치는 영향을 최소화합니다.',
    color: '#2e7d32',
    details: ['텀블러 세척으로 일회용 컵 소비 절감', '세척 데이터 기반 탄소 감축량 측정', '친환경 원료 및 재활용 가능 소재 사용'],
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
    title: '사회적 책임',
    description: '우리는 공정 거래와 인권 보호를 지지하며, 공급망 전반에 걸쳐 윤리적 기준을 준수합니다.',
    color: '#1565c0',
    details: ['청소 노동자의 근무 환경 개선', '인체공학적 설계로 산업재해 예방', '공정한 노동 환경 조성 기여'],
  },
  {
    icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
    title: '혁신과 품질',
    description: '우리는 혁신적인 기술과 디자인을 통해 지속 가능한 제품을 개발하고, 고객에게 최고의 품질을 제공합니다.',
    color: '#e65100',
    details: ['IOT 기반 실시간 데이터 수집', 'AI 분석 기반 환경 성과 리포트', '지속적인 하드웨어 R&D 투자'],
  },
];

const TIMELINE = [
  {
    year: '2021',
    title: '다끌레어 창립',
    desc: '지속 가능한 환경과 사람을 위한 스마트 청소 솔루션을 목표로 다끌레어가 설립됩니다.',
    isHighlight: false,
  },
  {
    year: '2022',
    title: '청소카트 혁신제품 지정',
    desc: '다끌레어 청소카트가 공공조달 혁신제품으로 지정되며 시장에서 인정받습니다.',
    isHighlight: false,
  },
  {
    year: '2023',
    title: '텀블러 세척기 출시',
    desc: 'QR 기반 데이터 수집 텀블러 세척기를 출시하고 첫 기관 설치를 시작합니다.',
    isHighlight: true,
  },
  {
    year: '2024',
    title: 'ENV-REPORT & ENV-AD 런칭',
    desc: '환경 성과 리포트 자동화 서비스(ENV-REPORT)와 친환경 광고 플랫폼(ENV-AD)을 정식 출시합니다.',
    isHighlight: false,
  },
  {
    year: '2025',
    title: '탄소중립마켓(VCM) 오픈',
    desc: '자발적 탄소 크레딧 거래 플랫폼을 오픈하여 기업의 탄소중립 실천을 지원합니다.',
    isHighlight: false,
  },
  {
    year: '2030',
    title: '100만 톤 CO₂ 감축 목표',
    desc: '다끌레어 네트워크를 통한 누적 탄소 감축량 100만 톤 달성을 목표로 합니다.',
    isHighlight: false,
  },
];

export default function MissionPage() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <Box>
      {/* ───── 히어로 ───── */}
      <Box
        ref={ref}
        component="section"
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #050a12 0%, #0d1b2a 50%, #091624 100%)',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(33,150,243,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(33,150,243,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(ellipse 65% 55% at 50% 40%, rgba(21,101,192,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box
          component={motion.div}
          style={{ y: textY, opacity: bgOpacity }}
          sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: { xs: 3, md: 6 }, maxWidth: '860px', width: '100%', py: { xs: 12, md: 0 } }}
        >
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <Chip
              label="MISSION"
              size="small"
              sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '11px', letterSpacing: '3px', backgroundColor: 'rgba(33,150,243,0.15)', color: '#90caf9', border: '1px solid rgba(33,150,243,0.3)', mb: 3 }}
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '28px', sm: '42px', md: '56px' }, color: '#ffffff', lineHeight: 1.15, letterSpacing: { xs: '-1px', md: '-2px' }, mb: 3 }}>
              우리는 지구와 사람을 위한<br />
              <Box component="span" sx={{ background: 'linear-gradient(90deg, #90caf9, #42a5f5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                지속 가능한 미래를 만듭니다
              </Box>
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 300, fontSize: { xs: '15px', md: '18px' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, mb: 6, maxWidth: '600px', mx: 'auto' }}>
              다끌레어는 환경 보호와 사회적 책임을 경영의 중심에 두고,<br />
              지속 가능한 방식으로 고품질의 친환경 제품을 제공합니다.<br />
              우리는 미래 세대를 위해 더 나은 세상을 만들고자 합니다.
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate('/company/sustainability')}
                endIcon={<ArrowForwardIcon />}
                sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', backgroundColor: '#1565c0', boxShadow: '0 0 30px rgba(21,101,192,0.4)', '&:hover': { backgroundColor: '#0d47a1', boxShadow: '0 0 40px rgba(21,101,192,0.6)' } }}
              >
                지속가능성 보기
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/company/contact')}
                sx={{ fontFamily: 'NotoSansKR', fontWeight: 600, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.25)', '&:hover': { borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'rgba(255,255,255,0.06)' } }}
              >
                함께하기
              </Button>
            </Stack>
          </motion.div>
        </Box>

        <Box
          component={motion.div}
          style={{ opacity: bgOpacity }}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          sx={{ position: 'absolute', bottom: '36px', left: '50%', transform: 'translateX(-50%)', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', gap: '4px', zIndex: 2 }}
        >
          <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '11px', color: 'rgba(255,255,255,0.25)', letterSpacing: '3px', textTransform: 'uppercase' }}>Scroll</Typography>
          <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '20px' }} />
        </Box>
      </Box>

      {/* ───── 미션 가치 ───── */}
      <Box sx={{ backgroundColor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 10 } }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: blueGrey[900], mb: 1.5 }}>
                우리가 추구하는 세 가지 가치
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '15px', md: '17px' }, color: blueGrey[500], lineHeight: 1.8 }}>
                다끌레어의 모든 결정은 이 세 가지 원칙을 기반으로 합니다
              </Typography>
            </Box>
          </ScrollReveal>

          <Grid container spacing={4}>
            {MISSION_VALUES.map((item, i) => (
              <Grid size={{xs:12, md:4}} key={item.title}>
                <ScrollReveal delay={i * 0.15}>
                  <Box sx={{ p: 4, borderRadius: '24px', border: '1px solid #f0f0f0', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 20px 60px rgba(0,0,0,0.08)', transform: 'translateY(-6px)', borderColor: `${item.color}30` } }}>
                    <Box sx={{ width: 72, height: 72, borderRadius: '20px', backgroundColor: `${item.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, color: item.color }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '20px', color: blueGrey[900], mb: 2 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '14px', color: blueGrey[600], lineHeight: 1.85, mb: 3 }}>
                      {item.description}
                    </Typography>
                    <Divider sx={{ mb: 2.5 }} />
                    <Stack spacing={1.5}>
                      {item.details.map(detail => (
                        <Stack key={detail} direction="row" alignItems="flex-start" spacing={1}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: item.color, mt: '2px', flexShrink: 0 }} />
                          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '13px', color: blueGrey[600], lineHeight: 1.6 }}>
                            {detail}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ───── 연혁 타임라인 ───── */}
      <Box sx={{ backgroundColor: '#f4f6f8', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 10 } }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: blueGrey[900], mb: 1.5 }}>
                다끌레어의 여정
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '15px', md: '17px' }, color: blueGrey[500], lineHeight: 1.8 }}>
                미션을 향해 걸어온 발자취와 앞으로의 목표
              </Typography>
            </Box>
          </ScrollReveal>

          {/* 타임라인 */}
          <Box sx={{ position: 'relative' }}>
            {/* 중앙선 (데스크탑) */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', backgroundColor: grey[200], transform: 'translateX(-50%)' }} />

            {TIMELINE.map((item, i) => {
              const isLeft = i % 2 === 0;
              return (
                <Box key={item.year} sx={{ position: 'relative', mb: 4 }}>
                  <ScrollReveal delay={i * 0.1} direction={isLeft ? 'left' : 'right'}>
                    <Grid container alignItems="center">
                      {/* 왼쪽 내용 (짝수 인덱스) */}
                      <Grid size={{xs:12, md:5}} sx={{ display: { xs: 'none', md: isLeft ? 'block' : 'block' }, textAlign: { md: isLeft ? 'right' : 'left' }, pr: { md: isLeft ? 5 : 0 }, pl: { md: isLeft ? 0 : 5 }, order: { md: isLeft ? 1 : 1 } }}>
                        {isLeft && (
                          <Box>
                            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '13px', color: blue[500], letterSpacing: '2px', mb: 0.5 }}>
                              {item.year}
                            </Typography>
                            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '18px', color: blueGrey[900], mb: 1 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '14px', color: blueGrey[600], lineHeight: 1.7 }}>
                              {item.desc}
                            </Typography>
                          </Box>
                        )}
                      </Grid>

                      {/* 중앙 도트 */}
                      <Grid size={{xs:12, md:2}} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', order: { md: 2 } }}>
                        <Box sx={{ width: item.isHighlight ? 20 : 14, height: item.isHighlight ? 20 : 14, borderRadius: '50%', backgroundColor: item.isHighlight ? blue[600] : blue[200], border: `3px solid ${item.isHighlight ? blue[400] : '#ffffff'}`, boxShadow: item.isHighlight ? `0 0 0 4px ${blue[100]}` : 'none', zIndex: 1, flexShrink: 0 }} />
                      </Grid>

                      {/* 오른쪽 내용 */}
                      <Grid size={{xs:12, md:5}} sx={{ textAlign: { xs: 'center', md: isLeft ? 'left' : 'right' }, pl: { md: isLeft ? 5 : 0 }, pr: { md: isLeft ? 0 : 5 }, order: { md: 3 } }}>
                        {!isLeft && (
                          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Typography sx={{ textAlign:'left', fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '13px', color: blue[500], letterSpacing: '2px', mb: 0.5 }}>
                              {item.year}
                            </Typography>
                            <Typography sx={{ textAlign:'left', fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '18px', color: blueGrey[900], mb: 1 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ textAlign:'left', fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '14px', color: blueGrey[600], lineHeight: 1.7 }}>
                              {item.desc}
                            </Typography>
                          </Box>
                        )}
                        {/* 모바일 전용 */}
                        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 1, mb: 2, p: 3, borderRadius: '16px', backgroundColor: '#ffffff', border: '1px solid #e8ecf0', textAlign: 'left' }}>
                          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '12px', color: blue[500], letterSpacing: '2px', mb: 0.5 }}>
                            {item.year}
                          </Typography>
                          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '16px', color: blueGrey[900], mb: 1 }}>
                            {item.title}
                          </Typography>
                          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '13px', color: blueGrey[600], lineHeight: 1.7 }}>
                            {item.desc}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </ScrollReveal>
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ───── CTA ───── */}
      <Box sx={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 50%, #0d2137 100%)', py: { xs: 12, md: 16 } }}>
        <Container maxWidth="md">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: '#ffffff', mb: 2, lineHeight: 1.3 }}>
                다끌레어에 대해<br />더 많은 정보를 알고 싶다면
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 300, fontSize: { xs: '15px', md: '17px' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, mb: 6, maxWidth: '480px', mx: 'auto' }}>
                우리의 미션에 동참하거나, 제품 및 서비스에 대해<br />더 자세히 알아보세요.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate('/company/contact')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', backgroundColor: '#1565c0', boxShadow: '0 0 30px rgba(21,101,192,0.4)', '&:hover': { backgroundColor: '#0d47a1', boxShadow: '0 0 40px rgba(21,101,192,0.6)' } }}
                >
                  함께하기
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/company/sustainability')}
                  sx={{ fontFamily: 'NotoSansKR', fontWeight: 600, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.25)', '&:hover': { borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'rgba(255,255,255,0.06)' } }}
                >
                  지속가능성 보기
                </Button>
              </Stack>
            </Box>
          </ScrollReveal>
        </Container>
      </Box>
    </Box>
  );
}

